import React, { useState, useEffect } from 'react';
import { auditAPI } from '../lib/api';
import { formatDate, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  RefreshCw,
  History,
  Filter,
  User,
  FileText,
  AlertTriangle,
  Settings,
  Download,
  LogIn,
  LogOut,
  Edit,
  Plus,
  Trash,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  create: Plus,
  update: Edit,
  delete: Trash,
  '2fa_enabled': Shield,
  '2fa_disabled': Shield,
  sar_generated: FileText,
  default: History,
};

const ACTION_COLORS = {
  login: 'bg-green-500/10 text-green-400',
  logout: 'bg-gray-500/10 text-gray-400',
  create: 'bg-blue-500/10 text-blue-400',
  update: 'bg-yellow-500/10 text-yellow-400',
  delete: 'bg-red-500/10 text-red-400',
  '2fa_enabled': 'bg-purple-500/10 text-purple-400',
  '2fa_disabled': 'bg-orange-500/10 text-orange-400',
  sar_generated: 'bg-cyan-500/10 text-cyan-400',
};

const RESOURCE_TYPES = ['users', 'entities', 'transactions', 'alerts', 'cases', 'watchlists'];
const ACTIONS = ['login', 'logout', 'create', 'update', 'delete', '2fa_enabled', '2fa_disabled', 'sar_generated'];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchLogs();
  }, [page, filters.action, filters.resource_type, filters.date_from, filters.date_to]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(filters.action && { action: filters.action }),
        ...(filters.resource_type && { resource_type: filters.resource_type }),
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
      };
      const response = await auditAPI.list(params);
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || ACTION_ICONS.default;
    return Icon;
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || 'bg-gray-500/10 text-gray-400';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Audit Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString()} audit events recorded
          </p>
        </div>
        <Button variant="outline" onClick={fetchLogs} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select 
              value={filters.action || "all"} 
              onValueChange={(v) => setFilters({...filters, action: v === "all" ? "" : v})}
            >
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Actions</SelectItem>
                {ACTIONS.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.resource_type || "all"} 
              onValueChange={(v) => setFilters({...filters, resource_type: v === "all" ? "" : v})}
            >
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <SelectValue placeholder="Resource Type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Resources</SelectItem>
                {RESOURCE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              className="w-[160px] bg-black/20 border-white/10"
              placeholder="From"
            />

            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              className="w-[160px] bg-black/20 border-white/10"
              placeholder="To"
            />

            <Button 
              variant="ghost" 
              onClick={() => setFilters({ action: '', resource_type: '', date_from: '', date_to: '' })}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="glass">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading audit logs...</p>
            </div>
          ) : logs.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const ActionIcon = getActionIcon(log.action);
                  return (
                    <TableRow key={log.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActionColor(log.action)}`}>
                            <ActionIcon className="w-4 h-4" />
                          </div>
                          <span className="text-white capitalize">
                            {log.action.replace('_', ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {log.resource_type}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">
                            {log.resource_id?.slice(0, 8)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground font-mono">
                            {log.user_id?.slice(0, 8)}...
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {log.details && Object.keys(log.details).length > 0 
                            ? JSON.stringify(log.details).slice(0, 50) + '...'
                            : '-'
                          }
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-white">
                            {formatRelativeTime(log.timestamp)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(log.timestamp)}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <History className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No audit logs found</p>
              <p className="text-sm text-muted-foreground/50 mt-1">
                Audit logs will appear as actions are performed
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
